import { defineStyleConfig } from '@chakra-ui/react';
import omit from 'lodash-es/omit';
import type { SystemStyleInterpolation, SystemStyleObject } from '@chakra-ui/react';

const sltButtonVariants = ['primary', 'secondary', 'tertiary'] as const;
type SLTButtonVariant = (typeof sltButtonVariants)[number];

// TODO should be deprecated once existed checkout button theme aligned with new design system
const sltButtonNewVariants = [
    'newprimary',
    'newprimary-black',
    'newsecondary',
    'newtertiary',
    'newquantity-button',
    'select-button',
    'newchip',
    'newmenu-chip',
] as const;
type SLTButtonNewVariant = (typeof sltButtonNewVariants)[number];

// TODO Block should be deprecated when checkout button aligned with UI components
const disabled = {
    bg: 'slt-black.100',
    borderColor: 'slt-black.100',
    color: 'slt-black.600',
    pointerEvents: 'none',
    opacity: 1,
};

const shared: SystemStyleObject = {
    boxSizing: 'border-box',
    borderRadius: '4px',
    textStyle: 'slt-body-text-button',
    height: '40px',
    paddingLeft: '32px',
    paddingRight: '32px',
    borderWidth: '1px',
    borderStyle: 'solid',
    _focus: {
        boxShadow: 'none',
    },
    _disabled: disabled,
};

const sltVariants: Record<SLTButtonVariant, SystemStyleInterpolation> = {
    primary: (props) => ({
        ...shared,
        backgroundColor: 'slt-black.900',
        borderColor: 'slt-black.900',
        color: 'white',
        _hover: {
            backgroundColor: 'white',
            color: 'slt-black.900',
            borderColor: 'slt-black.900',
            _disabled: disabled,
        },
        _active: {
            borderWidth: '1px',
            borderColor: 'black',
            outlineStyle: 'solid',
            outlineColor: 'slt-black.900',
            outlineWidth: '1px',
        },
        _focusVisible: {
            borderWidth: '1px',
            borderColor: 'black',
            outlineStyle: 'solid',
            outlineColor: 'slt-black.900',
            outlineWidth: '1px',
        },
        ...(props.colorScheme === 'plain'
            ? {
                  _hover: {
                      backgroundColor: 'slt-black.900',
                      color: 'white',
                      borderColor: 'slt-black.900',
                  },
                  _active: { outlineWidth: '0px' },
                  _focusVisible: { outlineWidth: '0px' },
              }
            : {}),
    }),
    secondary: (props) => ({
        ...shared,
        backgroundColor: 'white',
        color: 'slt-black.900',
        borderColor: 'slt-black.900',
        _hover: {
            backgroundColor: 'slt-black.900',
            borderColor: 'slt-black.900',
            color: 'white',
            _disabled: disabled,
        },
        _active: {
            borderWidth: '1px',
            borderColor: 'slt-black.900',
            outlineStyle: 'solid',
            outlineColor: 'slt-black.900',
            outlineWidth: '1px',
        },
        _focusVisible: {
            borderWidth: '1px',
            borderColor: 'slt-black.900',
            outlineStyle: 'solid',
            outlineColor: 'slt-black.900',
            outlineWidth: '1px',
        },
        ...(props.colorScheme === 'plain'
            ? {
                  _hover: {
                      backgroundColor: 'white',
                      color: 'slt-black.900',
                      borderColor: 'slt-black.900',
                  },
                  _active: { outlineWidth: '0px' },
                  _focusVisible: { outlineWidth: '0px' },
              }
            : {}),
    }),
    tertiary: (props) =>
        props.colorScheme === 'text-link'
            ? {
                  textDecorationLine: 'underline',
                  textDecorationColor: 'slt-black.900',
                  color: 'slt-black.900',
                  textStyle: 'slt-body-text',
                  fontWeight: 400,
                  height: '22px',
                  paddingInline: '0',
                  verticalAlign: 'unset',
                  _focus: {
                      boxShadow: 'none',
                  },
              }
            : {
                  textDecorationLine: 'underline',
                  textDecorationThickness: '2px',
                  textDecorationColor: 'slt-black.200',
                  textUnderlineOffset: '6px',
                  color: 'slt-black.900',
                  textStyle: 'slt-body-text',
                  fontWeight: 500,
                  height: '22px',
                  backgroundColor: '#FFFFFF00',
                  _hover: { textDecorationColor: 'black', backgroundColor: '#FFFFFF01' },
                  _active: { textDecorationColor: 'black' },
                  _focusVisible: {
                      textDecorationColor: 'black',
                      boxShadow: 'none',
                  },
              },
};
// End of deprecation block

// next is updated design system

const newDisabled = {
    backgroundColor: 'slt-pepper.100',
    border: 'none',
    pointerEvents: 'all',
    cursor: 'not-allowed',
    opacity: 1,
    color: 'slt-pepper.1000',
};

const newShared: SystemStyleObject = {
    boxSizing: 'border-box',
    borderRadius: '4px',
    textStyle: 'slt-body-text-button',
    height: '40px',
    paddingTop: '0.5rem',
    paddingBottom: '0.5rem',
    paddingLeft: '2rem',
    paddingRight: '2rem',
    borderWidth: '1.5px',
    borderStyle: 'solid',
    _focus: {
        boxShadow: 'none',
    },
    _disabled: newDisabled,
};

const quantityDisabled = {
    opacity: 0.5,
    background: 'white',
};

const primaryActiveStates = {
    borderWidth: '1px',
    borderColor: 'slt-pepper.1000',
    outlineStyle: 'solid',
    outlineColor: 'slt-pepper.1000',
    outlineWidth: '1px',
};
const sltNewDesignSystem: Record<SLTButtonNewVariant, SystemStyleInterpolation> = {
    newprimary: {
        ...newShared,
        backgroundColor: 'slt-primary-green.1000',
        borderColor: 'transparent',
        color: 'white',
        _hover: {
            backgroundColor: 'slt-primary-green.900',
            _disabled: newDisabled,
        },
        _active: { ...primaryActiveStates, backgroundColor: 'slt-primary-green.1000' },
        _focusVisible: {
            ...primaryActiveStates,
            backgroundColor: 'slt-primary-green.1000',
        },
        _disabled: newDisabled,
        _loading: {
            backgroundColor: 'slt-primary-green.900',
            color: 'white',
            _disabled: {
                ...newDisabled,
                backgroundColor: 'slt-primary-green.900',
                color: 'white',
            },
        },
    },
    'newprimary-black': {
        ...newShared,
        backgroundColor: 'slt-pepper.1000',
        borderColor: 'transparent',
        color: 'white',
        _hover: {
            backgroundColor: 'slt-pepper.900',
            _disabled: newDisabled,
        },
        _active: { ...primaryActiveStates },
        _focusVisible: {
            ...primaryActiveStates,
        },
    },
    newsecondary: {
        ...newShared,
        borderWidth: '1px',
        backgroundColor: 'white',
        borderColor: 'slt-pepper.1000',
        color: 'slt-pepper.1000',
        _hover: {
            backgroundColor: 'slt-pepper.50',
            borderColor: 'slt-pepper.1000',
            _disabled: newDisabled,
        },
        _active: primaryActiveStates,
        _focusVisible: primaryActiveStates,
    },
    newtertiary: {
        textDecorationLine: 'underline',
        textDecorationThickness: '2px',
        textDecorationColor: 'slt-pepper.300',
        textUnderlineOffset: '4px',
        color: 'slt-pepper.1000',
        height: '22px',
        gap: '4px',
        _hover: {
            textDecorationColor: 'slt-pepper.1000',
        },
        _focus: {
            textDecorationColor: 'slt-pepper.1000',
            boxShadow: 'none',
        },
    },
    'newquantity-button': {
        ...newShared,
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
        backgroundColor: 'white',
        borderColor: 'slt-pepper.1000',
        color: 'slt-pepper.1000',
        minWidth: '2.25rem',
        height: '2.25rem',
        _hover: {
            backgroundColor: 'slt-pepper.50',
            borderColor: 'slt-pepper.1000',
            _disabled: quantityDisabled,
        },
        _disabled: quantityDisabled,
    },
    'select-button': {
        background: 'white',
        padding: '0.75rem',
        borderRadius: '4px',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'slt-pepper.700',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        color: 'slt-pepper.1000',
        flexWrap: 'wrap',
        height: 'unset',
        margin: 0,
        minWidth: '109px',

        _focusVisible: {
            outlineWidth: '2px',
            outlineColor: 'slt-pepper.1000',
            outlineOffset: '-1px',
        },

        _active: {
            outlineWidth: '2px',
            outlineColor: 'slt-pepper.1000',
            outlineOffset: '-1px',
        },

        _disabled: {
            borderColor: 'slt-pepper.700',
        },
    },

    newchip: {
        ...newShared,
        textStyle: 'body-small',
        textTransform: 'capitalize',
        paddingLeft: '0.5rem',
        paddingRight: '0.5rem',
        borderColor: 'slt-pepper.600',
        width: 'fit-content',
        minWidth: '44px',
        _hover: {
            background: 'slt-pepper.50',
        },
        _active: {
            outlineColor: 'slt-pepper.1000',
            outlineOffset: 0,
            background: 'white',
            outlineWidth: '2px',
            borderColor: 'transparent',
        },
        _focus: {
            outlineColor: 'slt-pepper.1000',
            outlineWidth: '2px',
            outlineOffset: 0,
            background: 'white',
            boxShadow: 'none',
            borderColor: 'transparent',
        },
    },
    'newmenu-chip': {
        ...omit(newShared, ['paddingLeft', 'paddingRight', 'paddingTop', 'paddingBottom']),
        textStyle: 'body-base',
        color: 'slt-pepper.1000',
        textTransform: 'capitalize',
        px: 3,
        py: 2,
        borderWidth: '1px',
        borderColor: 'slt-pepper.1000',
        width: 'fit-content',
        height: 'fit-content',
        _hover: {
            background: 'slt-pepper.50',
        },
    },
};

// used as mapped types for Button component to Text component, see Button.tsx
const textButtonCommonStyles = {
    color: 'slt-pepper.1000',
    verticalAlign: 'unset',
    height: 'unset',
    padding: 0,
    paddingInline: 0,
    paddingInlineStart: 0,
    paddingInlineEnd: 0,
};

export const Button = defineStyleConfig({
    baseStyle: {
        borderRadius: 'base',
        fontWeight: 'medium',
    },
    variants: {
        ...sltVariants,
        ...sltNewDesignSystem,
        solid: (props) =>
            props.colorScheme === 'blue'
                ? {
                      backgroundColor: 'blue.600',
                      color: 'white',
                      _hover: { bg: 'blue.700', _disabled: { bg: 'blue.300' } },
                      _active: { bg: 'blue.800' },
                      _disabled: { bg: 'blue.300' },
                  }
                : {},
        outline: (props) =>
            props.colorScheme === 'black'
                ? { color: 'gray.900', _hover: { bg: 'gray.50' }, borderColor: 'gray.200' }
                : { color: 'blue.600', _hover: { bg: 'gray.50' } },
        footer: {
            fontSize: 'sm',
            backgroundColor: 'gray.100',
            color: 'black',
            _hover: { bg: 'gray.200' },
            _active: { bg: 'gray.300' },
            paddingLeft: 3,
            paddingRight: 3,
        },
        link: (props) => ({
            color: props.colorScheme === 'red' ? 'red.500' : 'blue.600',
            fontWeight: 'normal',
            minWidth: '1em',
            lineHeight: 4,
        }),
        'menu-link': {
            color: 'black',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            _hover: { bg: 'gray.50', textDecoration: 'none' },
            _activeLink: {
                bg: 'gray.50',
                textDecoration: 'none',
            },
        },
        'menu-link-mobile': {
            color: 'black',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            _hover: { bg: 'gray.50', textDecoration: 'none' },
            _activeLink: {
                bg: 'gray.100',
                textDecoration: 'none',
            },
        },
        'search-link': {
            color: 'black',
            justifyContent: 'flex-start',
            fontSize: 'sm',
            _hover: { textDecoration: 'none' },
        },
        'newtext-button': {
            ...textButtonCommonStyles,

            _hover: {
                color: 'slt-primary-green.1000',
                textDecorationColor: 'slt-primary-green.1000',
            },
        },
        'newtext-button-no-hover': {
            ...textButtonCommonStyles,
        },
        'newtext-button-no-underline': {
            ...textButtonCommonStyles,
        },
        'icon-button': {
            borderRadius: '50%',
            boxSizing: 'border-box',
            padding: 0,
            minWidth: 'unset',
            borderWidth: '1px',
            borderColor: 'transparent',
        },
    },
    sizes: {
        md: {
            height: 11,
            minWidth: 11,
        },
    },
    defaultProps: {
        variant: 'primary',
    },
});
